/**
 * ADT7 analytics — builds `window.analyticsObj` and GTM/DTM helper globals.
 */
import { getClientCookie, setClientCookie } from "@/lib/cookie/clientCookies";
import type { SelectedCampaign } from "@/lib/ecid/ecid";

import { DEFAULT_ECID } from "@/constants/analytics.constants";

const SITE_SECTION = "blu";

const DEFAULTS = {
  dnis: "WEB0004248",
  dnisChat: "WEB0004257",
  tfnSales: "800.521.1729",
  visitorSegment: "corp",
  ecid: "np",
  refType: "np",
} as const;

type PageFields = Pick<
  AnalyticsObject,
  "pageName" | "pageType" | "siteSection" | "formType"
>;

/** Exact-pathname overrides merged over the computed page fields. */
const PAGE_OVERRIDES: Record<string, Partial<PageFields>> = {
  "/": {
    pageName: "us | corp | home",
    siteSection: "resi",
  },
};

const PRE_PAGE_COOKIE = "pre_page_name";
const PRE_PAGE_COOKIE_MAX_AGE = "90d";

export function isMobileDevice(): boolean {
  const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  return hasTouch && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/** GTM/DTM-facing cookie reader — keeps the legacy `string | null` contract. */
function getCookieValue(name: string): string | null {
  return getClientCookie(name) ?? null;
}

function gaId(cookie: string | null): string | undefined {
  if (!cookie) return undefined;
  const second = cookie.indexOf(".", cookie.indexOf(".") + 1);
  return second === -1 ? undefined : cookie.slice(second + 1);
}

function campaignContact(
  campaign: SelectedCampaign | undefined,
  mobile: boolean,
) {
  if (!campaign) return { dnis: "", chat: "", tfn: "" };
  return mobile
    ? {
        dnis: campaign.dnisMobile ?? "",
        chat: campaign.chatMobile ?? "",
        tfn: campaign.tfnMobile ?? "",
      }
    : {
        dnis: campaign.dnisDesktop ?? "",
        chat: campaign.chatDesktop ?? "",
        tfn: campaign.tfnDesktop ?? "",
      };
}

let pendingFormEvent: Record<string, string> | undefined;

export function carryFormEventToNextPage(formEvent: Record<string, string>) {
  pendingFormEvent = formEvent;
}

/** Build analyticsObj and merge GA cookies when present. */
export function publishAdtAnalytics(input: {
  pathname: string;
  campaign?: SelectedCampaign | null;
}): AnalyticsObject {
  const mobile = isMobileDevice();
  const contact = campaignContact(input.campaign ?? undefined, mobile);

  const pageType = document.forms.length > 0 ? "Form Page" : "Content Page";
  const page: PageFields = {
    formType: pageType,
    pageName: buildPageName(input.pathname),
    pageType,
    siteSection: SITE_SECTION,
    ...PAGE_OVERRIDES[input.pathname],
  };
  const pick = (v: string, d: string) => (v !== "" ? v : d);

  const analytics: AnalyticsObject = {
    accountVariable: mobile ? "Mobile" : "Desktop",
    experience: mobile ? "Mobile" : "Desktop",
    ...page,
    dnis: pick(contact.dnis, DEFAULTS.dnis),
    dnisChat: pick(contact.chat, DEFAULTS.dnisChat),
    tfnSales: pick(contact.tfn, DEFAULTS.tfnSales),
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    visitorSegment: DEFAULTS.visitorSegment,
    gaObj: {},
    ecid: input.campaign?.ecid ?? DEFAULT_ECID,
    refType: input.campaign?.doc_ref_type ?? DEFAULTS.refType,
    prePageName: getCookieValue(PRE_PAGE_COOKIE) ?? page.pageName,
  };

  const gclId = gaId(getCookieValue("_gcl_aw"));
  const gaClientId = gaId(getCookieValue("_ga"));
  if (gclId) analytics.gaObj.gclId = gclId;
  if (gaClientId) analytics.gaObj.gaClientId = gaClientId;

  if (pendingFormEvent) {
    analytics.formEvent = pendingFormEvent;
    pendingFormEvent = undefined;
  }

  window.analyticsObj = analytics;
  return analytics;
}

let legacyGlobalsReady = false;

/** One-time GTM/DTM helpers (fetchDataForGA, CustomEventTrigger, etc.). */
export function ensureLegacyGlobals(): void {
  if (legacyGlobalsReady) return;
  legacyGlobalsReady = true;

  window.gaParse = (value: string) => gaId(value) ?? "";
  window.getCookieValue = getCookieValue;
  window.hasCookie = (name: string) => getClientCookie(name) != null;

  window.fetchDataForGA = (cookieName: string, variableName: string) => {
    const current = window.analyticsObj;
    const parsed = gaId(getCookieValue(cookieName));
    if (!current || !parsed) return;
    window.analyticsObj = {
      ...current,
      gaObj: { ...current.gaObj, [variableName]: parsed },
    };
  };

  window.updateDatalayer = (key: string, value: string) => {
    const current = window.analyticsObj;
    if (!current) return;
    window.analyticsObj = {
      ...current,
      customEvent: { ...(current.customEvent ?? {}), [key]: value },
    };
  };

  window.CustomEventTrigger = (
    name: string,
    values?: Record<string, unknown>,
  ) => {
    window.updateDatalayer?.("eventName", name);
    (document.getElementById("adt-body-id") ?? document.body).dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        cancelable: true,
        detail: values,
      }),
    );
  };

  window.teads_e = window.teads_e || [];

  if (typeof window.$ === "undefined") {
    const noop = () => ({
      find: noop,
      parent: noop,
      parents: noop,
      closest: noop,
      text: () => "",
      attr: () => "",
      val: () => "",
      length: 0,
    });
    (window as unknown as { $: typeof noop }).$ = noop;
  }
}

export function savePrePageName(): void {
  const name = window.analyticsObj?.pageName ?? "np";
  setClientCookie(PRE_PAGE_COOKIE, name, {
    maxAge: PRE_PAGE_COOKIE_MAX_AGE,
    sameSite: "lax",
  });
}

/**
 * Builds `pageName` / `prePageName` as `us | <siteSection> | <url path segments>`.
 * The pathname already includes the `/blu` storefront segment.
 * E.g: `/blu/monitoring` → `us | blu | blu | monitoring`.
 */
export function buildPageName(pathname: string): string {
  const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const urlSegments = normalizedPath
    .replace(/\/+/g, "/")
    .split("/")
    .filter(Boolean);
  return ["us", SITE_SECTION, ...urlSegments].join(" | ");
}

export type AnalyticsObject = {
  accountVariable: "Mobile" | "Desktop";
  experience: "Mobile" | "Desktop";
  formType: "Form Page" | "Content Page";
  pageName: string;
  pageType: "Form Page" | "Content Page";
  siteSection: string;
  dnis: string;
  dnisChat: string;
  tfnSales: string;
  viewportSize: string;
  visitorSegment: string;
  gaObj: { gclId?: string; gaClientId?: string };
  ecid: string;
  refType: string;
  prePageName: string;
  customEvent?: Record<string, string>;
  formEvent?: Record<string, string>;
};
